import React, { useState, useRef } from 'react';
import {
  View,
  Image,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Play, Pause } from '../../../icons';

interface NativeVideoProps {
  ref?: React.Ref<unknown>;
  source: { uri: string };
  style?: object;
  resizeMode?: string;
  paused?: boolean;
  onReadyForDisplay?: () => void;
  onError?: () => void;
  repeat?: boolean;
}

// react-native-video is a native module — on web it won't be available,
// so we guard with a try/catch require and fall back to an HTML5 video element.
let Video: React.ComponentType<NativeVideoProps> | null = null;
try {
   
  Video = require('react-native-video').default;
} catch {
  Video = null;
}

interface VideoRendererProps {
  fileUrl?: string;
  thumbnailUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  fileName: string;
  isActive: boolean; // pause when not current item
}

export const VideoRenderer: React.FC<VideoRendererProps> = ({
  fileUrl,
  thumbnailUrl,
  isLoading,
  hasError,
  fileName,
  isActive,
}) => {
  const [paused, setPaused] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<unknown>(null);

  // Pause when the carousel navigates away
  React.useEffect(() => {
    if (!isActive) setPaused(true);
  }, [isActive]);

  if (hasError || videoError) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>
          {videoError ? 'Video playback failed' : 'Failed to load video'}
        </Text>
        <Text style={styles.errorSub}>{fileName}</Text>
      </View>
    );
  }

  if (isLoading || !fileUrl) {
    return (
      <View style={styles.center}>
        {thumbnailUrl ? (
          <Image source={{ uri: thumbnailUrl }} style={styles.thumbnail} resizeMode="contain" />
        ) : null}
        <ActivityIndicator size="large" color="#fff" style={styles.spinnerOverlay} />
      </View>
    );
  }

  // Web: use native HTML5 video via createElement to avoid TypeScript issues
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        {React.createElement('video', {
          src: fileUrl,
          controls: true,
          autoPlay: false,
          style: {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            outlineStyle: 'none',
            outlineWidth: 0,
            display: 'block',
          },
        })}
      </View>
    );
  }

  // Native: react-native-video
  if (!Video) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Video playback not available</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Video
        ref={videoRef}
        source={{ uri: fileUrl }}
        style={styles.video}
        resizeMode="contain"
        paused={paused}
        onReadyForDisplay={() => setVideoReady(true)}
        onError={() => setVideoError(true)}
        repeat={false}
      />
      {!videoReady && thumbnailUrl && (
        <Image source={{ uri: thumbnailUrl }} style={[styles.video, styles.thumbnailOverlay]} resizeMode="contain" />
      )}
      <TouchableOpacity
        style={styles.playButton}
        onPress={() => setPaused(p => !p)}
        accessibilityLabel={paused ? 'Play video' : 'Pause video'}
        accessibilityRole="button"
      >
        {paused ? <Play size={48} color="#fff" /> : <Pause size={48} color="#fff" />}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  video: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  thumbnailOverlay: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.5,
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    opacity: 0.5,
  },
  playButton: {
    position: 'absolute',
    alignSelf: 'center',
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderRadius: 40,
    padding: 12,
  },
  spinnerOverlay: {
    position: 'absolute',
  },
  center: {
    flex: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 4,
  },
  errorSub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
});
